import {forwardRef} from 'react'
import {SharedMarkdownContent} from '@github-ui/code-view-shared/components/SharedMarkdownContent'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {MissingCombinationPlaceholder} from './MissingCombinationPlaceholder'
import {testIdProps} from '@github-ui/test-id-props'
import styles from './MarkdownContent.module.css'
import {Heading} from '@primer/react'

interface MarkdownContentProps {
  payload: SafeHTMLString
}

const MarkdownContent = forwardRef<HTMLDivElement, MarkdownContentProps>(({payload}, ref) => {
  const gettingStartedContent = payload

  return (
    <div className={styles.markdownContentWrapper} ref={ref} {...testIdProps('markdown-content')}>
      {/*
          We need to suppress the hydration warning because some of the HTML rendered here (e.g. clipboard copy)
          relies on behaviors/ non-React JS to replace meta- tags with other elements. When React goes to hydrate,
          it will see that the HTML has changed and throw an warning. We can safely suppress this warning because
          the content is static, so it's OK if the client re-render doesn't actually get written to the page.

          Right now we overriding the styles using the sx prop. In the future we want to handle this better.
      */}

      {gettingStartedContent ? (
        <>
          <Heading as="h2" className={styles.getStartedHeader}>
            Or set it up yourself...
          </Heading>
          <Heading as="h3" className={styles.gettingStartedSubtitle}>
            Get started
          </Heading>
          <div className="readme pb-3">
            <SharedMarkdownContent
              suppressHydrationWarning
              richText={gettingStartedContent}
              // eslint-disable-next-line @github-ui/github-monorepo/no-sx
              sx={{
                h2: {
                  fontSize: 2,
                  pb: 2,
                },
                h3: {
                  fontSize: 1,
                },
                p: {
                  fontSize: 1,
                },
                'a.btn': {
                  fontSize: '14px !important',
                },
                code: {
                  fontSize: 0,
                },
                '.highlight pre': {
                  fontSize: 0,
                },
                pre: {
                  borderColor: 'border.muted',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderRadius: 2,
                },
                '.highlight': {
                  'clipboard-copy': {
                    top: 0,
                    right: 0,
                    width: 32,
                    height: 32,
                    position: 'absolute',
                  },
                },
                'pre code': {
                  fontSize: 0,
                },
                '.anchor': {
                  visibility: 'hidden',
                },
                '.snippet-clipboard-content': {
                  'clipboard-copy': {
                    top: 0,
                    right: 0,
                    width: 32,
                    height: 32,
                    position: 'absolute',
                    '.octicon': {
                      marginRight: 0,
                    },
                  },
                },
              }}
            />
          </div>
        </>
      ) : (
        <MissingCombinationPlaceholder />
      )}
    </div>
  )
})

MarkdownContent.displayName = 'MarkdownContent'

export default MarkdownContent
